import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { WedgeProduct } from '@ekhodealer/ekho-common';
import { Button, Checkbox, Spinner, ToggleCard, ToggleCardGroup } from '@ekhodealer/ekho-common/rev';

import type { Selection } from 'react-aria-components';

import { formatCurrency } from '@common/lib/currency';
import { paths } from '@config/paths';

import { useGetProductPricing } from '../api/useGetProductPricing';
import {
	onboardingRecordIdAtom,
	onboardingStepAtom,
	selectedProductIdsAtom,
} from '../atoms/onboardingAtoms';
import { PRODUCT_BY_ID, PRODUCT_CATALOG } from '../constants/productCatalog';

import ProductSelectionSummary from './ProductSelectionSummary';

import './OnboardingSteps.scss';

/**
 * Product selection (multi-select)
 *
 * Shows all available products as toggle cards.
 *
 * **New dealers (Path 1):** Step 2 of 3. Gate: must have an onboarding record.
 * **Existing dealers (Path 2):** Step 1 of 2. Reachable via "Previous" from the payment step.
 */
export default function OnboardingProductsStep() {
	const navigate = useNavigate();
	const setStep = useSetAtom(onboardingStepAtom);
	const [selectedIds, setSelectedIds] = useAtom(selectedProductIdsAtom);
	const recordId = useAtomValue(onboardingRecordIdAtom);
	const { svpId } = useParams();
	const isExistingDealer = !!svpId;

	const { data: livePricing } = useGetProductPricing(svpId);

	// Set step index on mount — step 1 for new dealers, step 0 for existing
	useEffect(() => {
		setStep(isExistingDealer ? 0 : 1);
	}, [setStep, isExistingDealer]);

	// Gate: redirect to step 1 if no record exists (new dealers only)
	useEffect(() => {
		if (!recordId && !isExistingDealer) {
			navigate(paths.onboarding.getHref(), { replace: true });
		}
	}, [recordId, isExistingDealer, navigate]);

	// Pre-select AI BDC on first visit if nothing is selected
	const hasPreSelected = useRef(false);
	useEffect(() => {
		if (!hasPreSelected.current && selectedIds.size === 0) {
			hasPreSelected.current = true;
			setSelectedIds(new Set([WedgeProduct.AI_BDC]));
		}
	}, [selectedIds.size, setSelectedIds]);

	/** Resolve display price in cents for a product: live pricing > catalog fallback. */
	const getDisplayPrice = (productId: string): number => {
		const live = livePricing?.[productId]?.displayPrice;
		if (live !== undefined) return live;
		return PRODUCT_BY_ID.get(productId)?.priceMonthly ?? 0;
	};

	const totalMonthly = useMemo(() => {
		let total = 0;
		for (const id of selectedIds) {
			total += getDisplayPrice(id);
		}
		return total;
		// eslint-disable-next-line react-hooks/exhaustive-deps -- livePricing is a stable query result
	}, [selectedIds, livePricing]);

	const handleSelectionChange = (keys: Selection) => {
		if (keys === 'all') {
			setSelectedIds(new Set(PRODUCT_CATALOG.map((p) => p.id)));
		} else {
			setSelectedIds(new Set(keys as Set<string>));
		}
	};

	if (!recordId && !isExistingDealer) {
		return (
			<div className='onboarding-step onboarding-step--loading'>
				<Spinner size='medium' />
			</div>
		);
	}

	return (
		<div className='onboarding-step'>
			<div className='onboarding-step__header'>
				<h2>Choose your AI products</h2>
				<p>Select the products you want to activate for your dealership.</p>
			</div>

			<ToggleCardGroup
				selectionMode='multiple'
				selectedKeys={selectedIds}
				onSelectionChange={handleSelectionChange}
				disallowEmptySelection={false}>
				{PRODUCT_CATALOG.map((product) => (
					<ToggleCard
						key={product.id}
						id={product.id}
						isDisabled={product.disabled}>
						<div
							className={`onboarding-step__product-card${product.disabled ? ' onboarding-step__product-card--disabled' : ''}`}>
							<div className='onboarding-step__product-card-header'>
								<Checkbox
									isSelected={selectedIds.has(product.id)}
									isReadOnly
									isDisabled={product.disabled}
									aria-label={`Select ${product.name}`}
								/>
								<span className='onboarding-step__product-card-title'>
									{product.name}
								</span>
								{product.comingSoon ? (
									<span className='onboarding-step__product-card-badge'>
										Coming Soon
									</span>
								) : (
									<span className='onboarding-step__product-card-price'>
										{`${formatCurrency(getDisplayPrice(product.id) / 100, '—', false)}/mo`}
									</span>
								)}
							</div>
							<p className='onboarding-step__product-card-description'>
								{product.description}
							</p>
							<ul className='onboarding-step__product-card-features'>
								{product.features.map((feature) => (
									<li key={feature}>{feature}</li>
								))}
							</ul>
						</div>
					</ToggleCard>
				))}
			</ToggleCardGroup>

			<ProductSelectionSummary
					selectedIds={selectedIds}
					totalMonthly={totalMonthly}
					displayPrices={
						livePricing
							? Object.fromEntries(
								Object.entries(livePricing)
									.filter(([, p]) => p.displayPrice !== undefined)
									.map(([id, p]) => [id, p.displayPrice!]),
							)
							: undefined
					}
				/>

			<div className='onboarding-step__button-row'>
				<Button
					variant='filled'
					isDisabled={selectedIds.size === 0}
					onPress={() =>
						svpId
							? navigate(paths.svpSubscribe.getHref(svpId))
							: navigate(paths.onboardingSubscribe.getHref())
					}>
					Continue
				</Button>
				{!isExistingDealer && (
					<Button variant='outline' onPress={() => navigate(paths.onboarding.getHref())}>
						Previous
					</Button>
				)}
			</div>
		</div>
	);
}
